import { useMemo } from 'react';
import {
  useLocation,
  useMatches,
  useParams,
} from 'react-router-dom';
import {
  HomeIcon,
  InboxIcon,
  Cog6ToothIcon,
  UserGroupIcon,
  EnvelopeIcon,
  ArrowPathRoundedSquareIcon,
  IdentificationIcon,
  BoltIcon,
  LifebuoyIcon,
  ArrowTrendingUpIcon,
  PresentationChartLineIcon,
  DocumentMagnifyingGlassIcon,
  CurrencyDollarIcon,
  FolderIcon,
  PhoneIcon,
  UsersIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  DocumentCurrencyDollarIcon,
  DocumentPlusIcon,
  SparklesIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import {
  IPlanFeatures,
  OrganisationFeatureFlags,
  validatePolicySync,
} from 'shared';
import { PlusCircleIcon } from '@heroicons/react/20/solid';

import { env } from '../../common/lib/env';

import { useGroups } from './useGroups';
import { useGetSelectedOrganisation } from './useGetSelectedOrganisation';
import { useAccountingSystemTerm } from './useAccountingSystemTerm';
import { useUser } from './useUser';

interface INavOption {
  name: string;
  external?: boolean;
  href: string;
  type?: `groupOptionDefault` | `groupsOverview`;
  icon: typeof ArrowPathRoundedSquareIcon;
  current: boolean;
  /** The route requires a organisation to be connected and selected */
  requiresOrg?: boolean;
  /** Requires an active subscription, org must also be true */
  requiresSubscription?: boolean;
  /** Requires a paid feature */
  requiresPaidFeature?: keyof IPlanFeatures;
  alwaysOpen?: boolean;
  children?: INavOption[];
  devOnly?: boolean;
  demoTarget?: string;
  key?: string | number;
  invalidCount?: number;
  validCount?: number;
  memberCount?: number;
  absolute?: boolean;
}

interface INavigationOptsConfig {
  /**
   * Only show the default group in the nav. Used by the desktop sidebar,
   * where the other groups and "Add new group" live in the nested sidebar
   * (GroupsNav) instead.
   */
  onlyDefaultGroup?: boolean;
}

export function useNavigationOpts(orgConnected, config?: INavigationOptsConfig) {
  const user = useUser();
  const { pathname } = useLocation();
  const { groupId } = useParams();
  const { data: groups } = useGroups();
  const currentOrg = useGetSelectedOrganisation();
  const quotesTerminology = useAccountingSystemTerm(`Quotes`);
  const creditTerminology = useAccountingSystemTerm(`Credit Notes`);

  const currentGroupId = useMemo(() => {
    return groupId ? Number(groupId) : undefined;
  }, [groupId]);

  const onlyDefaultGroup = config?.onlyDefaultGroup;

  const newUIOpts: INavOption[] = useMemo(() => {
    const gs = groups ? [...groups] : [];

    // The main sidebar shows the single default group inline; when the org
    // has more groups, collapse them into one "Groups" entry instead (the
    // nested sidebar lists them all)
    const collapseGroups = onlyDefaultGroup && gs.length > 1;
    const visibleGroups = onlyDefaultGroup ? gs.filter(g => g.default) : gs;

    const groupOpts: INavOption[] = collapseGroups ? [{
      name: `Groups`,
      href: `/groups/${(gs.find(g => g.default) ?? gs[0]).id}`,
      icon: UserGroupIcon,
      current: false,
      requiresOrg: true,
      type: `groupsOverview`,
    }] : [...visibleGroups]
      .sort(a => a.default ? -1 : 1)
      .map(group => ({
        name: group.title,
        href: `/groups/${group.id}`,
        icon: UserGroupIcon,
        current: currentGroupId === group.id,
        requiresOrg: true,
        type: group.default ? `groupOptionDefault` : undefined,
        key: group.id,
        invalidCount: group.policies?.filter(p => {
          try {
            validatePolicySync(p);

            return false;
          }
          catch (e) {
            return true;
          }
        }).length,
        validCount: group.policies?.filter(p => {
          try {
            validatePolicySync(p);

            return true;
          }
          catch (e) {
            return false;
          }
        }).length,
        memberCount: group.contactsCount,
      }));

    const opts: INavOption[] = [
      {
        name: `Dashboard`,
        href: `/`,
        icon: HomeIcon,
        current: false,
        requiresOrg: false,
      },
      {
        name: `Reports`,
        href: `/reports`,
        icon: ClipboardDocumentListIcon,
        current: false,
        requiresOrg: false,
        absolute: true,
      },
      {
        name: `Group Workflows`,
        href: `SPACE`,
        current: false,
        icon: UserGroupIcon,
        requiresOrg: true,
      },
      ...groupOpts,
      // With only the default group shown, "Add new group" lives in the
      // nested sidebar (GroupsNav) instead
      ...(onlyDefaultGroup ? [] : [{
        name: `Add new group`,
        icon: PlusCircleIcon,
        current: false,
        requiresOrg: true,
        href: `ADD_NEW_GROUP`,
      }]),
      {
        name: ``,
        href: `SPACE`,
        requiresOrg: true,
        icon: Cog6ToothIcon,
        current: false,
      },
      {
        name: `Team Activity`,
        href: `/team-activity`,
        icon: ChartBarIcon,
        current: false,
        requiresOrg: true,
      },
      {
        name: `Email Performance`,
        href: `/email-performance`,
        icon: PresentationChartLineIcon,
        current: false,
        requiresOrg: true,
      },
      {
        name: ``,
        href: `SPACE`,
        requiresOrg: true,
        icon: Cog6ToothIcon,
        current: false,
      },
      {
        name: `Contacts`,
        icon: IdentificationIcon,
        current: false,
        href: `/contacts/manage`,
        requiresOrg: true,
        demoTarget: `contacts`,
      },
      {
        name: `Invoices`,
        href: `/invoices`,
        icon: DocumentCurrencyDollarIcon,
        current: false,
        requiresOrg: true,
      },
      {
        name: quotesTerminology,
        href: `/quotes`,
        icon: DocumentTextIcon,
        current: false,
        requiresOrg: true,
      },
      {
        name: creditTerminology,
        href: `/credits`,
        icon: DocumentPlusIcon,
        current: false,
        requiresOrg: true,
      },
      {
        name: ``,
        href: `SPACE`,
        requiresOrg: true,
        icon: Cog6ToothIcon,
        current: false,
      },
      {
        name: `Inbox`,
        href: `/inbox`,
        icon: InboxIcon,
        current: false,
        requiresOrg: true,
      },
      {
        name: `Actions`,
        href: `/actions`,
        icon: BoltIcon,
        current: false,
        requiresOrg: true,
        demoTarget: `actions`,
      },
      {
        name: `Escalations`,
        href: `/escalations`,
        icon: ArrowTrendingUpIcon,
        current: false,
        requiresOrg: true,
      },
      {
        name: `Agents`,
        href: `/agents`,
        icon: SparklesIcon,
        current: false,
        requiresOrg: true,
      },
      {
        name: `Payments`,
        href: `/payments`,
        icon: CurrencyDollarIcon,
        current: false,
        requiresOrg: true,
      },
      {
        name: `Calls`,
        href: `/calls`,
        icon: PhoneIcon,
        current: false,
        requiresOrg: true,
      },
      {
        name: `Settings`,
        href: `SPACE`,
        icon: Cog6ToothIcon,
        current: false,
        requiresOrg: true,
      },
      {
        name: `Organisation Settings`,
        icon: Cog6ToothIcon,
        href: `/settings/general`,
        current: false,
        requiresOrg: true,
        demoTarget: `organisationSettings`,
      },
      {
        name: `Email Templates`,
        icon: EnvelopeIcon,
        current: false,
        href: `/settings/email-templates`,
        requiresOrg: true,
        demoTarget: `emailTemplates`,
      },
      {
        name: `Files`,
        icon: FolderIcon,
        href: `/settings/files`,
        current: false,
        requiresOrg: true,
      },
      {
        name: `Users`,
        href: `/settings/users`,
        icon: UsersIcon,
        current: false,
        requiresOrg: true,
      },
      {
        name: `Audit Logs`,
        href: `/logs`,
        current: false,
        requiresOrg: true,
        icon: DocumentMagnifyingGlassIcon,
      },
      {
        name: ``,
        href: `SPACE`,
        icon: Cog6ToothIcon,
        current: false,
      },
      {
        name: `Book a Call`,
        href: `BOOK_DEMO`,
        icon: PhoneIcon,
        current: false,
        external: true,
        demoTarget: `helpCenter`,
      },
      {
        name: `Help Center`,
        href: `https://support.paidnice.com?source=app-nav`,
        icon: LifebuoyIcon,
        current: false,
        external: true,
        demoTarget: `helpCenter`,
      },
    ];

    return opts.filter(i => {
      if (!env.legacyPolicyUser) {
        // Remove the fake policy option
        if (i.name === `Policies`) {
          return false;
        }
      }

      if (i.requiresOrg && !orgConnected) {
        return false;
      }

      return true;
    });

  }, [groups, pathname, orgConnected, currentOrg, quotesTerminology, user, onlyDefaultGroup]);

  const matches = useMatches();

  return {
    navigation: newUIOpts
      .filter(i => {
        if (i.href === `/payments`) {
        // Only show payments if the user has the payments feature
          if (!currentOrg?.stripeAccount?.id) {
            return false;
          }
        }

        if (
          i.href === `/calls` &&
          !currentOrg?.featureFlags?.includes(OrganisationFeatureFlags.CLICK_TO_CALL)
        ) {
          return false;
        }

        if (
          i.href === `/agents` &&
          !currentOrg?.featureFlags?.includes(OrganisationFeatureFlags.AI_AGENT)
        ) {
          return false;
        }

        return true;
      })
      .map(i => {
        let current = false;

        if (i.href === `/`) {
          current = pathname === `/`;
        }
        else {
        // Not pretty but adding some known checks
          if (i.href === `/actions`) {
            current = pathname.split(`/`)?.[1] === `actions`;
          }
          else if (i.href === `/team-activity`) {
            // Also active on a user's drill-down page
            current = pathname.split(`/`)?.[2] === `team-activity`;
          }
          else if (i.type === `groupsOverview`) {
            // Collapsed "Groups" entry - active on any group's page
            current = pathname.split(`/`)?.[2] === `groups`;
          }
          else {
            current = matches.some(m => m.pathname.endsWith(i.href));
          }
        }

        return {
          ...i,
          current,
        };
      }),
  };
}
